import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { TagChip } from '@/components/shared/tag-chip'
import { ManageStagesLink } from '@/components/shared/manage-links'
import { apiError } from '@/lib/api-client'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { useOrgMembers, useTags } from '@/hooks/use-settings'
import { useBulkContacts, type BulkContactBody } from '@/hooks/use-contacts'

/** Thanh thao tác hàng loạt trên các khách đã chọn: gắn / gỡ nhãn, đổi giai đoạn, giao phụ trách. */
export function CustomersBulkBar({ ids, canAssign, onClear }: { ids: string[]; canAssign: boolean; onClear: () => void }) {
  const { t } = useTranslation('custList')
  const bulk = useBulkContacts()
  const { stages } = useLifecycleStages()
  const { data: tags = [] } = useTags()
  const { data: members = [] } = useOrgMembers(canAssign)

  const run = (body: BulkContactBody, done: string) =>
    bulk.mutate(body, {
      onSuccess: (r) => {
        toast.success(t('bulkBar.result', { done, changed: r.changed, selected: r.selected }))
        // Giai đoạn đích có trường bắt buộc: khách còn thiếu bị bỏ qua — nói rõ để biết tự mở từng khách mà điền.
        if (r.skippedMissing) {
          toast.warning(t('bulkBar.skipped', { count: r.skippedMissing, fields: (r.skippedMissingFields ?? []).join(', ') }), {
            description: t('bulkBar.skippedHint'),
          })
        }
        if (body.action === 'set_stage' || body.action === 'assign') onClear()
      },
      onError: (e) => toast.error(apiError(e)),
    })

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-primary px-3 py-2 text-primary-foreground" role="toolbar" aria-label={t('bulkBar.aria')}>
      <b className="mr-1 text-sm">{t('bulkBar.selected', { count: ids.length })}</b>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="secondary" disabled={bulk.isPending}>{t('bulkBar.addTag')}</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-h-72 overflow-y-auto">
          {tags.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">{t('bulkBar.noTags')}</p>}
          {tags.map((tag) => (
            <DropdownMenuItem key={tag.id} onSelect={() => run({ action: 'add_tags', ids, tags: [tag.name] }, t('bulkBar.added', { name: tag.name }))}>
              <TagChip name={tag.name} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="secondary" disabled={bulk.isPending}>{t('bulkBar.removeTag')}</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-h-72 overflow-y-auto">
          {tags.map((tag) => (
            <DropdownMenuItem key={tag.id} onSelect={() => run({ action: 'remove_tags', ids, tags: [tag.name] }, t('bulkBar.removed', { name: tag.name }))}>
              <TagChip name={tag.name} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="secondary" disabled={bulk.isPending}>{t('bulkBar.setStage')}</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {stages.map((s) => (
            <DropdownMenuItem key={s.key} onSelect={() => run({ action: 'set_stage', ids, stage: s.key }, t('bulkBar.movedTo', { label: s.label }))}>
              <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color ?? '#64748b' }} /> {s.label}
            </DropdownMenuItem>
          ))}
          <ManageStagesLink />
        </DropdownMenuContent>
      </DropdownMenu>

      {canAssign && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="secondary" disabled={bulk.isPending}>{t('bulkBar.assign')}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-72 overflow-y-auto">
            <DropdownMenuLabel className="text-xs">{t('bulkBar.assignTo')}</DropdownMenuLabel>
            {members.map((m) => (
              <DropdownMenuItem key={m.id} onSelect={() => run({ action: 'assign', ids, assignedUserId: m.id }, t('bulkBar.assignedTo', { name: m.fullName ?? t('bulkBar.staffLower') }))}>
                {m.fullName ?? t('bulkBar.staff')}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => run({ action: 'assign', ids, assignedUserId: null }, t('bulkBar.unassigned'))}>{t('bulkBar.unassign')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Button size="sm" variant="ghost" className="ml-auto text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground" onClick={onClear}>
        {t('bulkBar.clear')}
      </Button>
    </div>
  )
}
